/**
 * sessionGoLive — make the session row say "live" before anyone is told it is.
 *
 * Every go-live surface (Event Designer step run, SessionDetail "open for
 * participants", the live session page's auto-start, the workspace wizard) sends
 * the same urgent `session_started` notification: title "Live now", body
 * "Session ready: … · Join now". Most of them never moved the row out of
 * `DRAFT`, so the alert pointed at a session the participant surface reports as
 * "The facilitator is still setting up this session."
 *
 * Prod evidence (2026-09-01, FBD workspace): every one of `tom@fbd.demo`'s seven
 * unread `session_started` rows resolved to a `DRAFT` or `RUN_COMPLETE` session;
 * one fired 16 minutes *after* its session completed, because re-entering a
 * finished step re-runs the invite path.
 *
 * So the transition and the announcement live together, in one place, and the
 * announcement is conditional on the transition.
 *
 * The state this reads has to come from the ROW, not the client store. Prod
 * evidence (2026-09-03, FBD workspace, on a deploy that already carried this
 * module): session 853babca was announced "Live now" three seconds after it was
 * created, and it has never left `DRAFT` — `criteria_finalized_at` is NULL and
 * `governance_events` holds no `session_state_transitioned` row for it, so the
 * transition was never even attempted. The only path that returns
 * `wentLive: true` without writing is the already-finalized short-circuit, which
 * was reading `useSessionStore`'s in-memory copy. A store that disagrees with
 * the row is exactly the case this guard exists to catch, so it no longer gets
 * to be the witness.
 */

export interface GoLiveResult {
  /** The row is in a state a participant can join. */
  wentLive: boolean;
  /** Why not, when not — a state-machine guard reason, surfaced to the caller. */
  error?: string;
}

/**
 * Move `sessionId` into `CRITERIA_FINALIZED` if it is still `DRAFT`.
 *
 * - already `CRITERIA_FINALIZED` → live, nothing written
 * - `RUN_COMPLETE` / `PUBLISHED` → **not** live. Re-opening the console for a
 *   step that already ran is not a go-live and must not re-announce a closed
 *   room.
 * - transition refused (e.g. no criteria selected) → not live, reason returned
 */
export async function ensureSessionJoinable(
  sessionId: string,
  actorId: string | undefined,
  options?: { participantCount?: number },
): Promise<GoLiveResult> {
  const { loadSessionJoinability } = await import('./notificationService');

  // Read the row, not the store. `unknown` means no row came back (deleted, or
  // the read failed) — never announce against that.
  const before = (await loadSessionJoinability([sessionId]))[sessionId];
  if (before === 'live') return { wentLive: true };
  if (before === 'ended') return { wentLive: false, error: 'Session has already run (the row reads ended)' };
  if (before !== 'not_started') {
    return { wentLive: false, error: 'Could not read session state' };
  }

  const { useSessionStore } = await import('../stores/prioritization/sessionStore');
  const store = useSessionStore.getState();
  if (!store.getSession(sessionId)) return { wentLive: false, error: 'Session not found' };

  try {
    const result = await store.transitionSessionState(
      sessionId,
      'CRITERIA_FINALIZED',
      actorId,
      options,
    );
    if (result?.success === false) {
      console.warn('[sessionGoLive] go-live transition refused:', result.error);
      return { wentLive: false, error: result.error };
    }
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Go-live transition failed';
    console.warn('[sessionGoLive] go-live transition threw:', e);
    return { wentLive: false, error: message };
  }

  // A reported-successful transition is not proof the row moved: the store
  // updates its own copy, and the urgent alert is the thing that must not lie.
  const after = (await loadSessionJoinability([sessionId]))[sessionId];
  if (after !== 'live') {
    const error = `Transition reported success but the row reads ${after ?? 'unreadable'}`;
    console.warn('[sessionGoLive]', error);
    return { wentLive: false, error };
  }
  return { wentLive: true };
}
